import EntityModel from '@/common/models/entity-model';
import SegmentDataModel from '@/common/models/segment-data-model';
import SegmentUserModel from '@/common/models/segment-user-model';
import unKebabCase from '@/common/helpers/un-kebab-case';

export default class SegmentModel extends EntityModel {
    $recordCount;

    static Scopes = {
        public: 'public',
        private: 'private',
        shared: 'shared',
    };

    static ScopeLabels = {
        public: 'All Users',
        private: 'Private',
        shared: 'Specific Users',
    };

    hydrate({
        id = null,
        data = null,
        scope = null,
        users = [],
        owner = {},
        isVisible = true,
        isStarred = false,
        $recordCount = null,
    } = {}) {
        this.id = id;
        this.data = new SegmentDataModel(data || {});
        this.scope = scope || SegmentModel.Scopes.public;
        this.owner = new SegmentUserModel(owner);
        this.users = users.map(user => new SegmentUserModel(user));
        this.isVisible = isVisible;
        this.isStarred = isStarred;
        this.$recordCount = $recordCount;
    }

    get sharedWith() {
        switch (this.scope) {
            case SegmentModel.Scopes.public:
                return SegmentModel.ScopeLabels.public;
            case SegmentModel.Scopes.shared:
                return SegmentModel.ScopeLabels.shared;
            default:
            case SegmentModel.Scopes.private:
                return SegmentModel.ScopeLabels.private;
        }
    }

    get userIds() {
        return this.users.map(user => user.id);
    }

    get isShared() {
        return this.scope === SegmentModel.Scopes.shared;
    }

    get isReadOnly() {
        return this.id.startsWith('read-only');
    }

    get isTemporary() {
        return this.id.startsWith('templog-');
    }

    isOwner(userId) {
        return this.owner.id === userId;
    }

    setUsersFromIds(userIds) {
        this.users = userIds.map(userId => new SegmentUserModel({id: userId}));
    }

    getPayload() {
        return {
            data: this.data.getPayload(),
            scope: this.scope,
            owner: this.owner,
            userIds: this.isShared
                ? this.users.map(user => user.id)
                : [],
            isVisible: this.isVisible,
            isStarred: this.isStarred,
            id: this.id,
        };
    }

    /**
     * Generate a segment for temporary use linked from a transaction details page.
     * @param {string} type
     * @param {string} resourceId
     * @param {string} ownerId
     * @param {Object} gridColumns
     * @returns {SegmentModel}
     */
    static createTemporarySegment({
        type, resourceId, ownerId, gridColumns,
    }) {
        // take default columns from definition and map their name
        const columns = typeof gridColumns[type] === 'object'
            ? gridColumns[type].defaultColumns.map(({name}) => name)
            : gridColumns[type](() => {}).defaultColumns.map(({name}) => name);
        // determine which field to target based on the type
        let field = '';
        switch (type) {
            case SegmentDataModel.Types.apiLogs:
                field = 'relatedIds.transactionId';
                break;
            case SegmentDataModel.Types.webhookLogs:
                field = 'entityId';
                break;
            case SegmentDataModel.Types.customers:
            default:
                field = 'tags';
        }
        return new SegmentModel({
            id: `templog-${type}-${resourceId}`,
            scope: SegmentModel.Scopes.private,
            data: new SegmentDataModel({
                name: `${unKebabCase(type)} for ${resourceId}`,
                page: SegmentDataModel.Pages.lists,
                type,
                filters: [[field, [resourceId]]],
                columns,
            }).getPayload(),
            owner: new SegmentUserModel({
                id: ownerId,
                name: '',
            }).getPayload(),
        });
    }
}
